import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react"

interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[]
    readonly userChoice: Promise<{
        outcome: 'accepted' | 'dismissed'
        platform: string
    }>
    prompt(): Promise<void>
}

export interface PWAState {
    isInstallable: boolean
    isInstalled: boolean
    handleInstall: () => Promise<void>
}

interface PWAContextType extends PWAState {}

const PWAContext = createContext<PWAContextType | null>(null)

export function PWAProvider({ children }: { children: ReactNode }) {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
    const [isInstallable, setIsInstallable] = useState(false)
    const [isInstalled, setIsInstalled] = useState(false)

    useEffect(() => {
        console.log('PWAProvider: Provider mounted, checking installation state')
        
        const handleBeforeInstallPrompt = (e: Event) => {
            console.log('PWAProvider: beforeinstallprompt event fired')
            e.preventDefault()
            setDeferredPrompt(e as BeforeInstallPromptEvent)
            setIsInstallable(true)
            console.log('PWAProvider: Install prompt deferred, app is installable')
        }

        const handleAppInstalled = () => {
            console.log('PWAProvider: App installed successfully')
            setIsInstalled(true)
            setIsInstallable(false)
            setDeferredPrompt(null)
        }

        // Check if already installed
        if (window.matchMedia('(display-mode: standalone)').matches) {
            console.log('PWAProvider: App is already installed (standalone mode)')
            setIsInstalled(true)
            return
        }

        console.log('PWAProvider: App not installed, setting up event listeners')
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
        window.addEventListener('appinstalled', handleAppInstalled)

        return () => {
            console.log('PWAProvider: Cleaning up event listeners')
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
            window.removeEventListener('appinstalled', handleAppInstalled)
        }
    }, [])

    const handleInstall = useCallback(async () => {
        console.log('PWAProvider: Install requested')
        
        if (!deferredPrompt) {
            console.warn('PWAProvider: No deferred prompt available')
            return
        }

        try {
            console.log('PWAProvider: Showing install prompt')
            await deferredPrompt.prompt()
            const { outcome } = await deferredPrompt.userChoice
            
            console.log('PWAProvider: User choice outcome:', outcome)

            if (outcome === 'accepted') {
                console.log('PWAProvider: User accepted installation')
                setIsInstallable(false)
                setDeferredPrompt(null)
            } else {
                console.log('PWAProvider: User dismissed installation')
            }
        } catch (error) {
            console.error('PWAProvider: Error installing PWA:', error)
        }
    }, [deferredPrompt])

    const value: PWAContextType = {
        isInstallable,
        isInstalled,
        handleInstall
    }

    return (
        <PWAContext.Provider value={value}>
            {children}
        </PWAContext.Provider>
    )
}

export function usePWA() {
    const context = useContext(PWAContext)
    if (!context) {
        throw new Error('usePWA must be used within a PWAProvider')
    }
    return context
}
